import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import AnalysisModule from '../components/AnalysisModule';
import ExpandableCard from '../components/ExpandableCard';
import History from '../components/History';
import { initFirebase, saveAnalysis, subscribeToHistory } from '../firebase';

const Mackinder01 = () => {
  const [userId, setUserId] = useState(null);
  const [authStatus, setAuthStatus] = useState('Cargando autenticación...');
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    const init = async () => {
      const uid = await initFirebase();
      if (uid) {
        setUserId(uid);
        setAuthStatus(`✅ Conectado`);
        
        const unsubscribe = subscribeToHistory(uid, (data) => {
          setAnalyses(data);
        });
        
        return () => unsubscribe();
      } else {
        setAuthStatus('⚠️ No hay usuario autenticado');
      }
    };
    
    init();
  }, []);

  const handleSave = (query, result, type) => {
    if (userId) {
      saveAnalysis(userId, query, result, type);
    }
  };

  return (
    <div className="container">
      <Header 
        title="Mackinder - Parte 1" 
        subtitle="Análisis Geopolítico Avanzado"
        authStatus={authStatus}
      />

      <section className="card" style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004e92', marginBottom: '1rem', textAlign: 'center' }}>
          El Pivote Geográfico de la Historia
        </h2>
        <p style={{ lineHeight: '1.7', marginBottom: '1rem', textAlign: 'center', maxWidth: '800px', margin: '0 auto 1.5rem' }}>
          En 1904, Mackinder presentó su teoría del "Pivote Geográfico de la Historia", argumentando que el control de Europa del Este era la clave para dominar el mundo.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <div className="grid">
          <ExpandableCard
            icon="🌍"
            title="La Zona Pivote Original (1904)"
            color="#004e92"
            summary="Mackinder identificó inicialmente la 'Zona Pivote' como el área de Eurasia inaccesible al poder naval británico."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Esta región incluía:</p>
                <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li><strong>Imperio Ruso:</strong> El núcleo de la zona pivote con sus vastos territorios asiáticos</li>
                  <li><strong>Asia Central:</strong> Turkestán, Mongolia y las estepas</li>
                  <li><strong>Siberia:</strong> Occidental y oriental, rica en recursos naturales</li>
                  <li><strong>Meseta del Tíbet:</strong> Región montañosa estratégica</li>
                </ul>
                <p style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem' }}>
                  💡 <strong>Dato clave:</strong> Esta zona representaba aproximadamente 21 millones de km², casi el 16% de la superficie terrestre del planeta.
                </p>
              </div>
            }
          />

          <ExpandableCard
            icon="⚔️"
            title="Contexto Histórico"
            color="#8b5cf6"
            summary="La teoría surgió en un momento crucial de transformación geopolítica global."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Factores determinantes:</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #ef4444' }}>
                    <p style={{ fontWeight: 'bold', color: '#ef4444' }}>🎯 El Gran Juego (1830-1895)</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>Rivalidad estratégica entre el Imperio Británico y el Imperio Ruso por el control de Asia Central.</p>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #3b82f6' }}>
                    <p style={{ fontWeight: 'bold', color: '#3b82f6' }}>🚂 Revolución Ferroviaria</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>El Transiberiano (1891-1916) permitió a Rusia proyectar poder terrestre sin depender del mar.</p>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(234, 179, 8, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #eab308' }}>
                    <p style={{ fontWeight: 'bold', color: '#eab308' }}>⚓ Declive del Poder Naval</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>Las potencias terrestres comenzaron a desafiar la supremacía naval británica.</p>
                  </div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #8b5cf6' }}>
                    <p style={{ fontWeight: 'bold', color: '#8b5cf6' }}>🇩🇪 Unificación Alemana (1871)</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>Alemania emergió como nueva potencia terrestre en el corazón de Europa.</p>
                  </div>
                </div>
              </div>
            }
          />
        </div>
      </section>

      <section className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem', color: '#004e92', textAlign: 'center' }}>
          💡 La Tesis de 1904
        </h3>
        <div style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', padding: '1.5rem', borderRadius: '0.5rem', borderLeft: '4px solid #3b82f6' }}>
          <p style={{ fontSize: '1.1rem', fontStyle: 'italic', lineHeight: '1.8', marginBottom: '1rem' }}>
            "El poder que controle la Zona Pivote comandará el mayor de los recursos naturales y humanos del mundo, y por lo tanto, será capaz de dominar el mundo."
          </p>
          <p style={{ textAlign: 'right', color: '#94a3b8' }}>- Halford Mackinder, 1904</p>
        </div>
      </section>

      <AnalysisModule
        title="✨ Análisis Histórico"
        description="Analice un período histórico específico bajo la teoría de Mackinder."
        inputType="textarea"
        systemPrompt="Actúa como un historiador especializado en geopolítica. Analiza el período histórico proporcionado usando la Teoría del Heartland de Mackinder. Explica cómo el control o intento de control del Heartland influyó en los eventos. Responde en español de forma concisa."
        onSave={handleSave}
        type="section"
      />

      <History analyses={analyses} />

      <div style={{ position: 'fixed', bottom: '20px', right: '20px', display: 'flex', gap: '10px', zIndex: 1000 }}>
        <Link to="/mackinder" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          ⬅️ Anterior
        </Link>
        <Link to="/" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          🏠 Inicio
        </Link>
        <Link to="/mackinder02" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          Siguiente ➡️
        </Link>
      </div>
    </div>
  );
};

export default Mackinder01;
